import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ConversationSummary, ExportConversationResponse } from '@dermalink/shared';
import { useAuthFetch } from '@/hooks';
import { getInitialsFromName, inactiveClosesOnLabel, triggerDownload } from '@/utils';
import { avatarPalette, conversationTimeLabel } from './conversationLabels.ts';
import { CheckboxIcon, CheckCircleIcon, DownloadIcon, EuroCircleIcon, MessageIcon, WarningTriangleIcon } from './icons';
import styles from './ConversationCard.module.css';


interface Props {
  conv: ConversationSummary;
  /** Pre-formatted display name shown as the card title (e.g. "Dr. Smith" or "Jane Doe"). */
  displayName: string;
  selected?: boolean;
  onClick: () => void;
}

function CardStatusIcon({ conv }: { conv: ConversationSummary }) {
  if (conv.flagged_inactive_at) {
    const closesOn = inactiveClosesOnLabel(conv.flagged_inactive_at);
    return (
      <span
        role="img"
        aria-label={`Inactief gesprek — verloopt ${closesOn}`}
        title={`Dit gesprek is inactief. Zonder nieuw bericht wordt het automatisch afgesloten op ${closesOn}.`}
      >
        <WarningTriangleIcon className={`${styles.statusIcon} ${styles.statusIconWarning}`} size={18} />
      </span>
    );
  }
  if (conv.status === 'closure_requested') return <CheckCircleIcon className={`${styles.statusIcon} ${styles.statusIconSuccess}`} size={18} />;
  if (conv.status === 'payment_requested') return <EuroCircleIcon className={`${styles.statusIcon} ${styles.statusIconPay}`} size={18} />;
  if (conv.status === 'active' || conv.status === 'resolution_rejected') return <MessageIcon className={styles.statusIcon} size={18} />;
  return null;
}

/** Closed-only: star rating stacked above the PDF download status, in the same column width as a single status icon. */
function ClosedStatusColumn({ conv }: { conv: ConversationSummary }) {
  const authFetch = useAuthFetch();
  const queryClient = useQueryClient();
  const downloaded = !conv.pdf_download_pending;
  const tone = downloaded ? styles.pdfStatusDone : styles.pdfStatusPending;

  const exportMutation = useMutation({
    mutationFn: async () => {
      const res = await authFetch(`/conversations/${conv.id}/export`, { method: 'POST' });
      if (!res.ok) throw new Error('Kon de PDF niet genereren. Probeer opnieuw.');
      const body = (await res.json()) as ExportConversationResponse;
      return body.url;
    },
    // Optimistic and permanent by design — this is a reminder aid, not a strict
    // download receipt, so it ticks on click and doesn't revert on failure.
    onMutate: () => {
      queryClient.setQueryData<ConversationSummary[]>(['doctor-conversations'], (old) =>
        old?.map((c) => c.id === conv.id ? { ...c, pdf_download_pending: false } : c) ?? []
      );
    },
    onSuccess: (url) => {
      if (!url) return;
      triggerDownload(url);
    },
  });

  return (
    <div className={styles.closedStatusCol}>
      {conv.star_rating != null && (
        <span className={styles.starRating} aria-label={`${conv.star_rating} van 5 sterren`}>
          {'★'.repeat(conv.star_rating)}{'☆'.repeat(5 - conv.star_rating)}
        </span>
      )}
      <button
        type="button"
        className={`${styles.pdfStatus} ${exportMutation.isError ? styles.pdfStatusPending : tone}`}
        aria-label={exportMutation.isError ? 'PDF downloaden mislukt, opnieuw proberen' : downloaded ? 'PDF opnieuw downloaden' : 'PDF downloaden'}
        title={exportMutation.isError ? 'PDF downloaden mislukt. Klik om opnieuw te proberen.' : downloaded ? 'PDF opnieuw downloaden' : 'PDF downloaden'}
        disabled={exportMutation.isPending}
        onClick={(e) => {
          e.stopPropagation();
          exportMutation.mutate();
        }}
      >
        <CheckboxIcon checked={downloaded} size={11} />
        <DownloadIcon size={13} />
      </button>
    </div>
  );
}

export function ConversationCard({ conv, displayName, selected = false, onClick }: Props) {
  const initials = getInitialsFromName(displayName);
  const palette = avatarPalette(displayName);
  return (
    <div
      className={`${styles.card}${selected ? ` ${styles.cardSelected}` : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
    >
      <span className={styles.avatar} style={{ background: palette.bg, color: palette.color }}>{initials}</span>
      <div className={styles.info}>
        <div className={styles.nameRow}>
          <span className={styles.name}>{displayName}</span>
          {conv.unread_count > 0 && (
            <span className={styles.unreadBadge} aria-label={`${conv.unread_count} ongelezen berichten`}>
              {conv.unread_count > 99 ? '99+' : conv.unread_count}
            </span>
          )}
        </div>
        <span className={styles.timeLabel}>{conversationTimeLabel(conv)}</span>
      </div>
      {conv.status === 'closed' ? <ClosedStatusColumn conv={conv} /> : <CardStatusIcon conv={conv} />}
    </div>
  );
}
